/**
 * 账单导入的**端侧纯逻辑**（`DR-051` · 批 86 · `/import`）
 *
 * ## 🔴 本文件**不做任何解析**
 *
 * 编码识别、CSV 解析、列识别、去重判定、分类建议、腿结构 —— 全在共享层
 * `nucleus/import.ts`（**唯一实现**），服务端调用它。端侧只做三件"形态"事：
 *
 * | 做 | 不做 | 为什么 |
 * |---|---|---|
 * | 上传前的文件形态拦截（大小 / 扩展名） | 判"这份账单能不能解析" | 后者要读字节 → 必须先解码，而解码是服务端内核的事 |
 * | 把服务端建议**搬进**可编辑状态 | 自己推测分类 | `AC-D1.6` 的置信度与冲突提示都由服务端给 |
 * | 只把**用户改过的**行放进落库请求 | 把整表回传 | `AC-D5.2`：未列出的行用服务端建议（少传 = 少一个不一致面） |
 *
 * ## 🔴 为什么"只发改过的行"不是优化，而是口径
 *
 * 若端侧把整表都传回去，就当于**用端侧的副本覆盖服务端的解析结果**：一旦用户在上传后
 * 又改了某笔金额（本版不支持改金额），或服务端在两次请求之间升级了建议算法，
 * 落库结果会与预览展示不一致，且**看不出是谁改的**。⇒ 未改动 = 不回传。
 */

import {
  CategoryKind,
  ImportPolicy,
  type CategoryView,
  type ImportBatchView,
  type ImportCommitRequest,
  type ImportPreviewResult,
  type ImportPreviewRow,
} from '@jiaying/shared';

// ═══════════════════════════ ① 上传前的形态拦截 ═══════════════════════════

/**
 * 本地就能判死的拒绝理由（`null` = 可以上传）。
 *
 * ⚠️ **只拦"形态"**：0 字节 / 超 10 MB / 扩展名不是 `.csv`（或 `.txt`）。
 * 服务端同一套上限还有一道（`ImportPolicy.MaxBytes` + `main.ts` 的 413 闸）——
 * 两处**不是重复实现**：这里是为了不让用户白等 10 MB 上传，服务端那道才是裁决。
 */
export function fileRejectReason(file: { name: string; size: number }): string | null {
  if (file.size === 0) return '文件是空的（0 字节）';
  if (file.size > ImportPolicy.MaxBytes) {
    // 🔴 这里**只报字节数**，不做 MB 换算：端侧封死了 `/` 与 `toFixed`（`AC-P1.2`），
    //    而为了显示一个「约 X MB」去加 `eslint-disable` 并不值得 —— 上限本身已写在文案里。
    return `文件 ${String(file.size)} 字节，超过 10 MB 上限；请按月拆分后分批导入`;
  }
  const lower = file.name.toLowerCase();
  if (!lower.endsWith('.csv') && !lower.endsWith('.txt')) {
    return '请选择微信 / 支付宝导出的 CSV 文件（.csv 或 .txt）';
  }
  return null;
}

/** 编码选项（`auto` = 让服务端嗅探）—— 用户只在"自动识别失败"时才需要动它 */
export const IMPORT_ENCODING_CHOICES = [
  { value: 'auto', label: '自动识别（推荐）' },
  { value: 'utf-8', label: 'UTF-8' },
  { value: 'gbk', label: 'GBK（老版微信 / 支付宝导出）' },
  { value: 'gb18030', label: 'GB18030' },
] as const;

/** `auto` ⇒ `undefined`（**不发 `encoding` 键**，让服务端嗅探 —— 铁律 63：不确定就不发） */
export function encodingParam(choice: string): string | undefined {
  return choice === 'auto' ? undefined : choice;
}

// ═══════════════════════════ ② 展示文案（唯一来源在服务端枚举） ═══════════════════════════

/** 事件 → 中文（**只翻译，不判断** —— `event` 由服务端解析得出） */
export function eventLabel(event: string): string {
  switch (event) {
    case 'expense':
      return '支出';
    case 'income':
      return '收入';
    case 'transfer':
      return '转账';
    case 'refund':
      return '退款';
    default:
      // 🔴 未知事件**原样回显**：静默显示"支出"会把未来的新事件类型伪装成最坏的情况
      return event;
  }
}

/** 置信度档位 → 中文（`high` / `mid` / `low`） */
export function confidenceBandLabel(band: string): string {
  switch (band) {
    case 'high':
      return '高';
    case 'mid':
      return '中';
    case 'low':
      return '低';
    default:
      return band;
  }
}

/**
 * 重复标记文案（`null` = 没有重复标记）。
 *
 * 🔴 **L1 与 L2/L3 的语义不同**，文案必须分开：L1 是"已存在，不导入"，
 * L2/L3 是"疑似重复，但仍会导入"（`AC-D2.1` vs `AC-D2.3`）。
 */
export function duplicateText(level: ImportPreviewRow['duplicateLevel']): string | null {
  switch (level) {
    case 'L1':
      return '已存在，不导入';
    case 'L2':
      return '疑似重复（仍会导入）';
    case 'L3':
      return '可能重复（仍会导入）';
    default:
      return null;
  }
}

/** 预览统计条（**纯搬运服务端字段**，不做加减） */
export function previewSummary(p: ImportPreviewResult): string {
  return (
    `解析 ${String(p.counts.parsed)} 笔 · 将跳过重复 ${String(p.counts.willSkip)} 笔` +
    (p.counts.skipped > 0 ? ` · 无法识别 ${String(p.counts.skipped)} 行` : '') +
    (p.counts.suspect > 0 ? ` · 疑似重复 ${String(p.counts.suspect)} 笔` : '')
  );
}

// ═══════════════════════════ ③ 决策 → 落库请求 ═══════════════════════════

/** 一行的用户决策（初始 = 服务端建议） */
export interface RowDecision {
  /** `null` = 未分类（允许 —— `AC-D5.3` 未分类可后补） */
  categoryId: string | null;
  /** 是否把「商户 → 分类」写进规则（`AC-D3.8`） */
  remember: boolean;
}

/** 逐行初始决策 = 服务端建议（**端侧不猜分类**） */
export function initialDecisions(p: ImportPreviewResult): Map<number, RowDecision> {
  const m = new Map<number, RowDecision>();
  for (const row of p.rows) {
    m.set(row.gridRow, { categoryId: row.suggestedCategoryId, remember: false });
  }
  return m;
}

/** 会被真正落库的行（`willSkip` = L1 重复，服务端不会再落） */
export function importableRows(p: ImportPreviewResult): readonly ImportPreviewRow[] {
  return p.rows.filter((r) => !r.willSkip);
}

/**
 * 组装落库请求。
 *
 * 🔴 `rows` 只包含**用户改过分类**或**勾了"记住规则"**的行（上面文件头的口径）。
 * 一条都没有 ⇒ **整个 `rows` 键都不发**（不是发空数组 —— 空数组与"没传"在
 * 契约层语义相同，但少一个"空数组算不算合规"的争议点，铁律 63）。
 */
export function buildCommitRequest(
  preview: ImportPreviewResult,
  decisions: ReadonlyMap<number, RowDecision>,
): ImportCommitRequest {
  const rows: NonNullable<ImportCommitRequest['rows']>[number][] = [];

  for (const row of importableRows(preview)) {
    const d = decisions.get(row.gridRow);
    if (d === undefined) continue;
    const changed = d.categoryId !== row.suggestedCategoryId;
    // 记住规则必须有一个具体分类（未分类的规则毫无意义 —— 服务端也会忽略）
    const remember = d.remember && d.categoryId !== null;
    if (!changed && !remember) continue;
    rows.push({
      gridRow: row.gridRow,
      categoryId: d.categoryId,
      ...(remember ? { rememberRule: true } : {}),
    });
  }

  const base = { previewId: preview.previewId, accountId: preview.accountId };
  return rows.length === 0 ? base : { ...base, rows };
}

/** 「将导入 N 笔」按钮上的数字（**只数不跳过的行**，不做金额合计） */
export function importableCount(p: ImportPreviewResult): number {
  return importableRows(p).length;
}

// ═══════════════════════════ ④ 错误与批次 ═══════════════════════════

/**
 * 从 409 的错误详情里取「手工改过几笔」（`AC-D2.10`）。
 *
 * 🔴 **按结构化旗标读，不匹配 `message`**：文案随时会改，而 `details.editedCount`
 * 是契约（与批 83 的 `allocations` 校验同一条纪律 —— 铁律 44）。
 */
export function editedCountOfError(details: unknown): number | null {
  if (details === null || typeof details !== 'object') return null;
  const v = (details as { editedCount?: unknown }).editedCount;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** 批次状态中文 */
export function batchStatusText(batch: ImportBatchView): string {
  return batch.status === 'revoked' ? '已撤销' : '已导入';
}

/**
 * 撤销按钮的**禁用原因**（`null` = 可以撤）。
 *
 * 🔴 三条都由服务端字段派发：`revocable` 是服务端算的 30 天窗口结论（`AC-D2.9`），
 * 端侧**不自己比时间** —— 那会把"服务器时钟"与"浏览器时钟"混用。
 */
export function revokeBlockedReason(batch: ImportBatchView): string | null {
  if (batch.status === 'revoked') return '该批次已撤销';
  if (!batch.revocable) return `已超过 ${String(ImportPolicy.RevocableDays)} 天可撤销窗口`;
  if (batch.itemCount === 0) return '该批次没有流水';
  return null;
}

/**
 * 撤销前的提示语（撤销会删掉整批流水，必须说清"删几笔 / 其中几笔被改过"）。
 *
 * ⚠️ 这是**提醒**不是拦截：真正的拦截在服务端（有改过的记录时 409）。
 */
export function revokeHint(batch: ImportBatchView): string {
  const base = `将删除该批次导入的 ${String(batch.itemCount)} 笔流水（可在「流水」里逐笔恢复）。`;
  return batch.editedCount > 0 ? `${base}其中 ${String(batch.editedCount)} 笔被手工修改过。` : base;
}

// ═══════════════════════════ ⑤ 分类下拉（只做分组，不做判定） ═══════════════════════════

export interface ImportCategoryOption {
  id: string;
  label: string;
  matchText: string;
  /** 分组标题 = L1 名称（L1 自己归到自己名下，避免出现"孤儿组"） */
  group: string;
}

/**
 * 分类 → 下拉选项（**L1 + L2，带分组**）。
 *
 * 🔴 与 `CategoryPicker` 的 `toOptions()` **不是重复实现**：那个的 `indent` 用于
 * "缩进树"，本函数给的是 `group`（MUI 的分组标题）。两者要的信息不同，
 * 硬合并会让其中一个多出无用的字段（且 `CategoryOption` 没有 `group`）。
 */
export function importCategoryOptions(
  items: readonly CategoryView[],
  kind: CategoryKind,
): ImportCategoryOption[] {
  const l1 = items.filter((c) => c.kind === kind && c.level === 1);
  const out: ImportCategoryOption[] = [];
  for (const parent of l1) {
    out.push({ id: parent.id, label: parent.name, matchText: parent.name, group: parent.name });
    for (const child of items) {
      if (child.parentId !== parent.id) continue;
      out.push({
        id: child.id,
        label: child.name,
        matchText: child.name,
        group: parent.name,
      });
    }
  }
  return out;
}

/** 解析 `gridRow` → 分类名（未分类 / 找不到都给 `null`，由调用方显示占位） */
export function categoryNameOf(
  items: readonly CategoryView[],
  categoryId: string | null,
): string | null {
  if (categoryId === null) return null;
  return items.find((c) => c.id === categoryId)?.name ?? null;
}
